// API 基础配置
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

// 存储 JWT Token
let authToken: string | null = localStorage.getItem('admin_token');

// 设置认证 Token
export function setAuthToken(token: string | null) {
  authToken = token;
  if (token) {
    localStorage.setItem('admin_token', token);
  } else {
    localStorage.removeItem('admin_token');
  }
}

// 获取认证 Token
export function getAuthToken(): string | null {
  return authToken;
}

// 通用请求方法
async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };

  // 添加认证 Token
  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }

  try {
    const response = await fetch(url, {
      ...options,
      headers,
    });

    // 处理 401 未授权
    if (response.status === 401) {
      setAuthToken(null);
      // 可以在这里触发跳转到登录页
      throw new Error('未授权，请重新登录');
    }

    // 处理错误响应
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: '请求失败' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    return await response.json();
  } catch (err) {
    if (err instanceof Error) {
      throw err;
    }
    throw new Error('网络请求失败');
  }
}

// ========== 认证 API ==========

export async function login(username: string, password: string): Promise<{ token: string; user: { id: number; username: string } }> {
  const result = await request<{ token: string; user: { id: number; username: string } }>(
    '/auth/login',
    {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    }
  );
  
  // 保存 Token
  setAuthToken(result.token);
  
  return result;
}

export function logout() {
  setAuthToken(null);
}

// ========== 新闻文章 API ==========

export interface Article {
  id: number;
  title: string;
  date: string;
  category: string;
  summary: string;
  image: string;
  featured: boolean;
  content_blocks: any[];
  content_html?: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export async function fetchArticles(params?: {
  category?: string;
  featured?: boolean;
  limit?: number;
  offset?: number;
}): Promise<Article[]> {
  const query = new URLSearchParams();
  if (params?.category) query.append('category', params.category);
  if (params?.featured !== undefined) query.append('featured', String(params.featured));
  if (params?.limit) query.append('limit', String(params.limit));
  if (params?.offset) query.append('offset', String(params.offset));

  const result = await request<{ data: Article[] }>(
    `/articles${query.toString() ? '?' + query.toString() : ''}`
  );
  
  return result.data;
}

export async function fetchArticleById(id: number): Promise<Article> {
  const result = await request<{ data: Article }>(`/articles/${id}`);
  return result.data;
}

export async function createArticle(article: Omit<Article, 'id' | 'created_at' | 'updated_at'>): Promise<Article> {
  const result = await request<{ data: Article }>('/articles', {
    method: 'POST',
    body: JSON.stringify(article),
  });
  return result.data;
}

export async function updateArticle(id: number, article: Partial<Article>): Promise<void> {
  await request(`/articles/${id}`, {
    method: 'PUT',
    body: JSON.stringify(article),
  });
}

export async function deleteArticle(id: number): Promise<void> {
  await request(`/articles/${id}`, {
    method: 'DELETE',
  });
}

// ========== 团队管理 API ==========

export interface ManagementMember {
  id: number;
  name: string;
  position: string;
  city: string;
  store: string;
  experience: string;
  philosophy: string;
  image_prompt: string;
  image_url: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface FoundingMember {
  id: number;
  name: string;
  title: string;
  bio: string;
  tags: string[];
  image_prompt: string;
  image_url: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface CoreManagementMember {
  id: number;
  name: string;
  title: string;
  message: string;
  years: string;
  image_prompt: string;
  image_url: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export async function fetchManagementTeam(): Promise<ManagementMember[]> {
  const result = await request<{ data: ManagementMember[] }>('/teams/management');
  return result.data;
}

export async function saveManagementTeam(members: ManagementMember[]): Promise<void> {
  await request('/teams/management', {
    method: 'POST',
    body: JSON.stringify({ members }),
  });
}

export async function fetchFoundingTeam(): Promise<FoundingMember[]> {
  const result = await request<{ data: FoundingMember[] }>('/teams/founding');
  return result.data;
}

export async function saveFoundingTeam(members: FoundingMember[]): Promise<void> {
  await request('/teams/founding', {
    method: 'POST',
    body: JSON.stringify({ members }),
  });
}

export async function fetchCoreManagementTeam(): Promise<CoreManagementMember[]> {
  const result = await request<{ data: CoreManagementMember[] }>('/teams/core');
  return result.data;
}

export async function saveCoreManagementTeam(members: CoreManagementMember[]): Promise<void> {
  await request('/teams/core', {
    method: 'POST',
    body: JSON.stringify({ members }),
  });
}

// ========== 网站配置 API ==========

export interface SiteConfig {
  [key: string]: any;
}

export async function fetchSiteConfig(): Promise<SiteConfig> {
  const result = await request<{ data: SiteConfig }>('/config');
  return result.data;
}

export async function saveSiteConfig(config: SiteConfig): Promise<void> {
  await request('/config', {
    method: 'POST',
    body: JSON.stringify({ config }),
  });
}

export async function resetSiteConfig(): Promise<void> {
  await request('/config/reset', {
    method: 'POST',
  });
}

// ========== 文件上传 API ==========

export async function uploadImage(file: File): Promise<string> {
  const formData = new FormData();
  formData.append('image', file);

  const url = `${API_BASE_URL}/upload`;
  
  const headers: HeadersInit = {};
  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: '上传失败' }));
    throw new Error(error.error || '上传失败');
  }

  const result = await response.json();
  
  // 返回完整 URL
  const imageUrl = result.url;
  if (imageUrl.startsWith('/')) {
    // 相对路径，转换为完整 URL
    const baseUrl = window.location.origin;
    return `${baseUrl}${imageUrl}`;
  }
  
  return imageUrl;
}

export default {
  login,
  logout,
  setAuthToken,
  getAuthToken,
  fetchArticles,
  fetchArticleById,
  createArticle,
  updateArticle,
  deleteArticle,
  fetchManagementTeam,
  saveManagementTeam,
  fetchFoundingTeam,
  saveFoundingTeam,
  fetchCoreManagementTeam,
  saveCoreManagementTeam,
  fetchSiteConfig,
  saveSiteConfig,
  resetSiteConfig,
  uploadImage,
};
